import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@/store/features/baseRtkQuery';
import type {
    ClassificationDashboardResponse,
    FollowUpDashboardResponse,
    FollowUpTicketPage,
    RecoveryDashboardResponse,
    RecoveryTicketPage,
} from '@/app/ops/dashboards/freshdesk/types';

export type FreshdeskDashboardParams = {
    from?: string | null;
    to?: string | null;
    detailLimit?: number;
    /** Aggregate the preceding window of equal length too, for period-over-period deltas. */
    comparePrevious?: boolean;
    /** `day`, `hour`, or `week` for trend series. */
    granularity?: 'day' | 'hour' | 'week' | 'month';
};

export type FreshdeskTicketPageParams = FreshdeskDashboardParams & {
    group: string;
    value?: string;
    offset?: number;
    limit?: number;
};

function buildSearch(params?: FreshdeskDashboardParams) {
    const search = new URLSearchParams();
    if (params?.from) search.set('from', params.from);
    if (params?.to) search.set('to', params.to);
    if (params?.detailLimit) search.set('detailLimit', String(params.detailLimit));
    if (params?.comparePrevious === false) search.set('comparePrevious', 'false');
    if (params?.granularity) search.set('granularity', params.granularity);
    return search.toString();
}

function buildTicketSearch(params: FreshdeskTicketPageParams) {
    const search = new URLSearchParams(buildSearch(params));
    search.set('group', params.group);
    if (params.value) search.set('value', params.value);
    if (params.offset) search.set('offset', String(params.offset));
    if (params.limit) search.set('limit', String(params.limit));
    return search.toString();
}

export const opsFreshdeskApiSlice = createApi({
    reducerPath: 'opsFreshdeskApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['FreshdeskRecovery', 'FreshdeskClassification', 'FreshdeskFollowUp'],
    endpoints: (builder) => ({
        getRecoveryDashboard: builder.query<RecoveryDashboardResponse, FreshdeskDashboardParams | void>({
            query: (params) => ({
                url: `api/ops/freshdesk/dashboards/recovery?${buildSearch(params ?? undefined)}`,
                method: 'GET',
            }),
            providesTags: ['FreshdeskRecovery'],
        }),
        getFollowUpDashboard: builder.query<FollowUpDashboardResponse, FreshdeskDashboardParams | void>({
            query: (params) => ({
                url: `api/ops/freshdesk/dashboards/followup?${buildSearch(params ?? undefined)}`,
                method: 'GET',
            }),
            providesTags: ['FreshdeskFollowUp'],
        }),
        getRecoveryTickets: builder.query<RecoveryTicketPage, FreshdeskTicketPageParams>({
            query: (params) => ({
                url: `api/ops/freshdesk/dashboards/recovery/tickets?${buildTicketSearch(params)}`,
                method: 'GET',
            }),
        }),
        getFollowUpTickets: builder.query<FollowUpTicketPage, FreshdeskTicketPageParams>({
            query: (params) => ({
                url: `api/ops/freshdesk/dashboards/followup/tickets?${buildTicketSearch(params)}`,
                method: 'GET',
            }),
        }),
        getClassificationDashboard: builder.query<
            ClassificationDashboardResponse,
            FreshdeskDashboardParams | void
        >({
            query: (params) => ({
                url: `api/ops/freshdesk/dashboards/classification?${buildSearch(params ?? undefined)}`,
                method: 'GET',
            }),
            providesTags: ['FreshdeskClassification'],
        }),
    }),
});

export const opsFreshdeskApiActions = {
    useGetRecoveryDashboardQuery: opsFreshdeskApiSlice.useGetRecoveryDashboardQuery,
    useGetClassificationDashboardQuery: opsFreshdeskApiSlice.useGetClassificationDashboardQuery,
    useGetFollowUpDashboardQuery: opsFreshdeskApiSlice.useGetFollowUpDashboardQuery,
    useGetRecoveryTicketsQuery: opsFreshdeskApiSlice.useGetRecoveryTicketsQuery,
    useGetFollowUpTicketsQuery: opsFreshdeskApiSlice.useGetFollowUpTicketsQuery,
};
